import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Chip, Box } from '@mui/material';
import GenericDialog from 'src/components/GenericDialog';
import DynamicFormWidget from 'src/components/dynamic-widget/DynamicFormWidget';
import { DynamicField, DynamicFieldTypes } from 'src/components/dynamic-widget';
import RepeatingPropertiesField from 'src/components/dynamic-widget/RepeatingPropertiesField';
import { DataService } from 'src/services/data-service';
import { LovService, LovDictionary } from 'src/services/lov-service';
import { StorageUnit, StorageUnitDetail } from 'src/types/StorageUnit.types';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';
import { any } from 'prop-types';
import Loader from 'src/components/Loader';

interface Warehouse {
  Id: string;
  Name: string;
}

interface UpdateStorageUnitDrawerProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  updateData: StorageUnitDetail;
  warehouses: Warehouse[];
  storageUnits: StorageUnitDetail[];
  infoWarehouse?: { Code: string; Name: string };
  infoParentStorageUnit?: { Code: string; Name: string };
  forceParentWarehouseId?: string;
  forceParentStorageUnitId?: string;
  warehouseName?: string;
}

const UpdateStorageUnitDrawer: React.FC<UpdateStorageUnitDrawerProps> = (props) => {
  const {
    open,
    onClose,
    onSuccess,
    updateData,
    warehouses,
    storageUnits,
    infoWarehouse,
    infoParentStorageUnit,
    forceParentWarehouseId,
    forceParentStorageUnitId,
    warehouseName,
  } = props;
  
  const [formValues, setFormValues] = useState({
    Code: '',
    Name: '',
    ParentWarehouseId: forceParentWarehouseId || updateData?.warehouseId || '',
    ParentStorageUnitId: forceParentStorageUnitId || updateData?.parentStorageUnitId || '',
    StorageTypeId: '',
    Properties: [] as Array<{ key: string; value: string }>,
  });
  
  const [codeError, setCodeError] = useState<string | null>(null);
  const [predefinedValueOptions, setPredefinedValueOptions] = useState<LovDictionary>({});
  const [storageTypeOptions, setStorageTypeOptions] = useState<LovDictionary>({});
  const [loadingLovs, setLoadingLovs] = useState(false);

  const dispatch = useAppDispatch();

  const isLeaf = useMemo(() => {
    if (!updateData?.id) return true;
    return !storageUnits.some(su => su.parentStorageUnitId === updateData.id);
  }, [updateData, storageUnits]);

  useEffect(() => {
    // Handle properties mapping - if we have predefined values, try to map them back to IDs
    const properties = updateData?.properties 
      ? Object.entries(updateData.properties).map(([name, value]) => {
          // Try to find the predefined value ID that matches this property name
          const predefinedValueId = Object.keys(predefinedValueOptions).find(
            id => predefinedValueOptions[id] === name
          );
          return {
            key: predefinedValueId || name,
            value: String(value)
          };
        })
      : [];

    setFormValues({
      Code: updateData?.code || '',
      Name: updateData?.name || '',
      ParentWarehouseId: forceParentWarehouseId || updateData?.warehouseId || '',
      ParentStorageUnitId: forceParentStorageUnitId || updateData?.parentStorageUnitId || '',
      StorageTypeId: updateData?.storageTypeId || '',
      Properties: properties,
    });
    
    setCodeError(null);
  }, [updateData, forceParentWarehouseId, forceParentStorageUnitId, predefinedValueOptions]);

  useEffect(() => {
    if (open) {
      loadLovOptions();
    }
  }, [open]);

  const loadLovOptions = async () => {
    setLoadingLovs(true);
    try {
      const [predefinedValues, storageTypes] = await Promise.allSettled([
        LovService.getLovDictionary('all-preDefinedValues'),
        LovService.getLovDictionary('all-storage-types'),
      ]);

      setPredefinedValueOptions(predefinedValues.status === 'fulfilled' ? predefinedValues.value : {});
      setStorageTypeOptions(storageTypes.status === 'fulfilled' ? storageTypes.value : {});
    } catch (error) {
      console.error('Failed to load LOV options:', error);
      dispatch(showAlert({ message: 'Failed to load form options', type: 'error' }));
    } finally {
      setLoadingLovs(false);
    }
  };

  const filteredParentStorageUnits = useMemo(() => {
    return storageUnits.filter(
      su => su.warehouseId === (forceParentWarehouseId || formValues.ParentWarehouseId) && su.id !== updateData?.id
    );
  }, [storageUnits, formValues.ParentWarehouseId, forceParentWarehouseId, updateData]);

  const warehouseOptions = useMemo(() => {
    return warehouses.reduce((acc, warehouse) => {
      acc[warehouse.Id] = `${warehouse.Name}`;
      return acc;
    }, {} as Record<string, string>);
  }, [warehouses]);

  const storageUnitOptions = useMemo(() => {
    return filteredParentStorageUnits.reduce((acc, storageUnit) => {
      if (storageUnit.id) {
        acc[storageUnit.id] = `${storageUnit.name}`;
      }
      return acc;
    }, {} as Record<string, string>);
  }, [filteredParentStorageUnits]);

  const handleFieldChange = useCallback((field: string, value: any) => {
    setFormValues(prev => ({
      ...prev,
      [field]: field === 'Code' ? (typeof value === 'string' ? value.toUpperCase() : value) : value,
      ...(field === 'ParentWarehouseId' ? { ParentStorageUnitId: '' } : {}),
    }));
    if (field === 'Code') setCodeError(null);
  }, []);

  const formConfig: Record<string, DynamicFieldTypes> = useMemo(() => {
    const config: Record<string, DynamicFieldTypes> = {
      Code: {
        type: DynamicField.TEXT,
        required: false,
        name: 'Code',
        title: 'Code',
        value: formValues.Code,
        disabled: true,
        onChange: (val: string) => handleFieldChange('Code', val),
        helperText: null,
      },
      Name: {
        type: DynamicField.TEXT,
        required: true,
        name: 'Name',
        title: 'Name',
        disabled: false,
        value: formValues.Name,
        onChange: (val: string) => handleFieldChange('Name', val),
      },
      StorageTypeId: {
        type: DynamicField.SELECT,
        required: true,
        name: 'StorageTypeId',
        title: 'Storage Type',
        value: formValues.StorageTypeId,
        items: storageTypeOptions,
        onChange: (val: string) => handleFieldChange('StorageTypeId', val),
        disabled: loadingLovs,
      },
    };

    // Show current storage type name if available
    if (updateData?.storageType?.name) {
      config.CurrentStorageTypeName = {
        type: DynamicField.TEXT,
        required: false,
        name: 'CurrentStorageTypeName',
        title: 'Current Storage Type',
        value: updateData.storageType.name,
        disabled: true,
        onChange: () => {},
      };
    }

    // Always show warehouse (either as name or dropdown)
    if (warehouseName || updateData?.parentWarehouseName) {
      config.ParentWarehouseName = {
        type: DynamicField.TEXT,
        required: false,
        name: 'ParentWarehouseName',
        title: 'Warehouse',
        value: warehouseName || updateData?.parentWarehouseName || '',
        disabled: true,
        onChange: () => {},
      };
    } else if (!infoWarehouse && !forceParentWarehouseId) {
      config.ParentWarehouseId = {
        type: DynamicField.SELECT,
        required: false,
        name: 'ParentWarehouseId',
        title: 'Parent Warehouse',
        value: formValues.ParentWarehouseId,
        items: warehouseOptions,
        onChange: (val: string) => handleFieldChange('ParentWarehouseId', val),
        disabled: true,
      };
    }

    // Show current parent storage unit name if available
    if (updateData?.parentStorageUnitName && updateData.parentStorageUnitName !== '-') {
      config.CurrentParentStorageUnitName = {
        type: DynamicField.TEXT,
        required: false,
        name: 'CurrentParentStorageUnitName',
        title: 'Current Parent Storage Unit',
        value: updateData.parentStorageUnitName,
        disabled: true,
        onChange: () => {},
      };
    } else if (!infoParentStorageUnit && !forceParentStorageUnitId) {
      config.ParentStorageUnitId = {
        type: DynamicField.SELECT,
        required: false,
        name: 'ParentStorageUnitId',
        title: 'Parent Storage Unit',
        value: formValues.ParentStorageUnitId,
        items: { '': 'None', ...storageUnitOptions },
        onChange: (val: string) => handleFieldChange('ParentStorageUnitId', val),
        disabled: true,
      };
    }


    return config;
  }, [
    formValues,
    infoWarehouse,
    infoParentStorageUnit,
    forceParentWarehouseId,
    forceParentStorageUnitId,
    warehouseOptions,
    storageUnitOptions,
    handleFieldChange,
    warehouseName,
  ]);

  const formLayout = useMemo(() => {
    const basicFields = ['Code', 'Name'];
    if (updateData?.storageType?.name) {
      basicFields.push('CurrentStorageTypeName');
    }
    basicFields.push('StorageTypeId');

    const groups = [
      {
        title: 'Basic Info',
        fields: basicFields,
        columns: any as any,
      }
    ];

    const extraFields = [];
    if (warehouseName || updateData?.parentWarehouseName) extraFields.push('ParentWarehouseName');
    else if (!infoWarehouse && !forceParentWarehouseId) extraFields.push('ParentWarehouseId');
    
    if (updateData?.parentStorageUnitName && updateData.parentStorageUnitName !== '-') {
      extraFields.push('CurrentParentStorageUnitName');
    } else if (!infoParentStorageUnit && !forceParentStorageUnitId) {
      extraFields.push('ParentStorageUnitId');
    }
    
    if (extraFields.length > 0) {
      groups.push({
        title: 'Parent Assignment',
        fields: extraFields,
        columns: 2 as 2
      });
    }

    return { groups };
  }, [
    infoWarehouse,
    forceParentWarehouseId,
    infoParentStorageUnit,
    forceParentStorageUnitId,
    warehouseName,
    isLeaf,
    updateData?.storageType?.name,
    updateData?.parentStorageUnitName,
  ]);

  const handleSubmit = useCallback(async (data: Record<string, any>): Promise<boolean> => {
    try {
      const submitData: any = {
        id: updateData?.id,
        name: formValues.Name,
        storageTypeId: formValues.StorageTypeId,
        properties: {}
      };

      if (isLeaf) {
        const properties = data.Properties || formValues.Properties || [];
        properties.forEach((property: { key: string; value: string }) => {
          if (property.key && property.value) {
            const predefinedValueName = predefinedValueOptions[property.key] || property.key;
            submitData.properties[predefinedValueName] = property.value;
          }
        });
      }

      const endpoint = 'api/StorageUnit';
      const successMessage = 'Storage Unit updated!';
      const response = await DataService.put(endpoint, submitData);
      const result = await response.json();
      
      if (response.ok && result.success) {
        onSuccess();
        dispatch(showAlert({ message: successMessage, type: 'success' }));
        return true;
      } else {
        dispatch(showAlert({ message: result.message || 'An error occurred!', type: 'error' }));
        return false;
      }
    } catch (error) {
      dispatch(showAlert({ message: 'An unexpected error occurred!', type: 'error' }));
      return false;
    }
  }, [formValues, predefinedValueOptions, updateData, onSuccess, dispatch, isLeaf]);

        if(loadingLovs){
        return (
          <GenericDialog title="Add Shipment" open={open} onClose={onClose}>
            <Loader 
              variant="section"
              type="warehouse"
              size="medium"
              message="Loading Options..."
              subMessage="Please wait while we process your request"
              showProgress={true}
            />
          </GenericDialog>
        );
      }


  return (
    <GenericDialog 
      title={'Edit Storage Unit'} 
      open={open} 
      onClose={onClose}
    >
      {loadingLovs && isLeaf && (
        <Box sx={{ textAlign: 'center', py: 2, color: 'text.secondary' }}>
          Loading form options...
        </Box>
      )}

      {!isLeaf && (
        <Box 
          mb={2} 
          p={2} 
          sx={{ 
            backgroundColor: 'warning.light',
            color: 'warning.contrastText',
            borderRadius: 1,
            fontSize: '0.875rem'
          }}
        >
          <strong>Note:</strong> Group rules are not available for storage units that have child units.
        </Box>
      )}
      
      {(infoWarehouse || infoParentStorageUnit) && (
        <Box 
          mb={3} 
          p={2} 
          sx={{ 
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            boxShadow: 1
          }}
        >
          <Box display="flex" flexDirection="column" gap={1}>
            {infoWarehouse && (
              <Box display="flex" alignItems="center" gap={1}>
                <Box 
                  component="span" 
                  sx={{ 
                    fontWeight: 600, 
                    color: 'text.secondary',
                    minWidth: 120
                  }}
                >
                  Warehouse:
                </Box>
                <Chip 
                  label={`${infoWarehouse.Code} - ${infoWarehouse.Name}`}
                  color="primary" 
                  variant="outlined" 
                  size="small"
                  sx={{ fontWeight: 500 }} 
                />
              </Box>
            )}
            {infoParentStorageUnit && (
              <Box display="flex" alignItems="center" gap={1}>
                <Box 
                  component="span" 
                  sx={{ 
                    fontWeight: 600, 
                    color: 'text.secondary',
                    minWidth: 120
                  }}
                >
                  Parent Storage Unit:
                </Box>
                <Chip 
                  label={`${infoParentStorageUnit.Code} - ${infoParentStorageUnit.Name}`}
                  color="secondary" 
                  variant="outlined" 
                  size="small"
                  sx={{ fontWeight: 500 }} 
                />
              </Box>
            )}
          </Box>
        </Box>
      )}

      <DynamicFormWidget
        title=""
        drawerMode
        fields={formConfig}
        onSubmit={handleSubmit}
        initialValues={formValues}
        layout={formLayout}
      >
        {isLeaf && (
          <RepeatingPropertiesField
            value={formValues.Properties}
            onChange={(properties) => handleFieldChange('Properties', properties)}
            predefinedValueOptions={predefinedValueOptions}
            disabled={loadingLovs}
            title="Properties"
          />
        )}
      </DynamicFormWidget>
    </GenericDialog>
  );
};

export default UpdateStorageUnitDrawer;
